/* eslint-disable no-unused-vars */
import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import {
    DEFAULT_TRANSACTIONS_OPTIONS,
    PACKAGES_PER_PAGE_OPTIONS,
    PACKAGE_QUERY_OPTIONS,
    PACKAGE_SORT_OPTIONS,
    PACKAGE_DIR_OPTIONS,
    PACKAGE_PREDEFINED_OPTIONS,
    FREE_USERS_PLAN_NAME,
    VISIBILITY_ACCOUNT,
    VISIBILITY_SENDER
} from '../../../constants';
import {getRecipientsEmailsFromPackage} from '../../utils/helpers';
import {fetchAvatarsByEmails} from '../Packages/Recipients/recipientsSlice';
import {getIsSenderRecipient} from '../Packages/Recipients/utils';
import {getErrorMessage} from '../../utils/requests/error';
import {selectActiveAccountId} from '@components/Login/userSlice';
import {documentVisibilityApi} from '@api/documentVisibility/api';
import {transactionApi} from '@api/transaction/api';
import {recipientApi} from '@api/recipient/api';
import {documentApi} from '@api/document/api';
import {getApprovalsForTransactionByDocumentId} from '@components/Packages/utils/getApprovalsAndFields';
import {auditApi} from '@api/audit/api';

export const transactionsSlice = createSlice({
    name: 'transactions',
    initialState: {
        transactions: [],
        count: 0,
        transactionsPerPage: PACKAGES_PER_PAGE_OPTIONS[10],
        visibility: {},
        approvalsByTransactionId: {},
        fetchingApprovalsByTransactionId: {},
        isTransactionsFetching: false,
        isUpdatingTransaction: false,
        isDeletingTransaction: false,
        isReminderSaving: false,
        fetchingTransactionsError: null,
        updatingTransactionError: null,
        deletingTransactionError: null,
        reminderSavingError: null,
        isVisibilityFetching: false,
        fetchingVisibilityError: null,
        options: DEFAULT_TRANSACTIONS_OPTIONS,
        isNextToSignTransactionFetching: false,
        fetchingNextToSignTransactionError: null,
        transactionsSummary: {},
        isTransactionsSummaryFetching: false,
        fetchingTransactionsSummaryError: null,
        recentlyUpdatedTransactions: [],
        isRecentlyUpdatedTransactionsFetching: false,
        fetchingRecentlyUpdatedTransactionsError: null,
        transactionsAuditEvents: {},
        isTransactionAuditEventsFetching: {}
    },
    reducers: {
        setTransactions: (state, action) => {
            state.transactions = action.payload;
        },
        setCount: (state, action) => {
            state.count = action.payload;
        },
        setTransactionsPerPage: (state, action) => {
            state.transactionsPerPage = action.payload;
        },
        setVisibility: (state, action) => {
            state.visibility = action.payload;
        },
        setApprovalsForTransaction: (state, action) => {
            const {transactionId, documentApprovals} = action.payload;
            state.approvalsByTransactionId = {
                ...state.approvalsByTransactionId,
                [transactionId]: {
                    ...(state.approvalsByTransactionId[transactionId] || {}),
                    ...documentApprovals
                }
            };
        },
        setFetchingApprovalsForTransaction: (state, action) => {
            const {transactionId, isFetching} = action.payload;
            state.fetchingApprovalsByTransactionId = {
                ...state.fetchingApprovalsByTransactionId,
                [transactionId]: isFetching
            };
        },
        updateTransactionDocuments: (state, action) => {
            const {transactionId, documents} = action.payload;
            const index = state.transactions.findIndex(({id}) => id === transactionId);
            if (index !== -1) {
                state.transactions[index] = {
                    ...state.transactions[index],
                    documents
                };
            }
        },
        setIsTransactionsFetching: (state, action) => {
            state.isTransactionsFetching = action.payload;
        },
        setIsUpdatingTransaction: (state, action) => {
            state.isUpdatingTransaction = action.payload;
        },
        setIsDeletingTransaction: (state, action) => {
            state.isDeletingTransaction = action.payload;
        },

        setFetchingTransactionsError: (state, action) => {
            state.fetchingTransactionsError = action.payload;
        },
        setUpdatingTransactionError: (state, action) => {
            state.updatingTransactionError = action.payload;
        },
        setDeletingTransactionError: (state, action) => {
            state.deletingTransactionError = action.payload;
        },
        setIsVisibilityFetching: (state, action) => {
            state.isVisibilityFetching = action.payload;
        },
        setFetchingVisibilityError: (state, action) => {
            state.fetchingVisibilityError = action.payload;
        },

        setOptions: (state, action) => {
            state.options = action.payload;
        },
        setIsNextToSignTransactionFetching: (state, action) => {
            state.isNextToSignTransactionFetching = action.payload;
        },
        setFetchingNextToSignTransactionError: (state, action) => {
            state.fetchingNextToSignTransactionError = action.payload;
        },
        setTransactionsSummary: (state, action) => {
            state.transactionsSummary = action.payload;
        },
        setIsTransactionsSummaryFetching: (state, action) => {
            state.isTransactionsSummaryFetching = action.payload;
        },
        setFetchingTransactionsSummaryError: (state, action) => {
            state.fetchingTransactionsSummaryError = action.payload;
        },
        setRecentlyUpdatedTransactions: (state, action) => {
            state.recentlyUpdatedTransactions = action.payload;
        },
        setIsRecentlyUpdatedTransactionsFetching: (state, action) => {
            state.isRecentlyUpdatedTransactionsFetching = action.payload;
        },
        setFetchingRecentlyUpdatedTransactionsError: (state, action) => {
            state.fetchingRecentlyUpdatedTransactionsError = action.payload;
        },
        setTransactionAuditEvents: (state, action) => {
            const {transactionId, events} = action.payload;
            state.transactionsAuditEvents[transactionId] = events;
        },
        setIsTransactionAuditEventsFetching: (state, action) => {
            const {transactionId, isFetching} = action.payload;
            state.isTransactionAuditEventsFetching = {
                ...state.isTransactionAuditEventsFetching,
                [transactionId]: isFetching
            };
        }
    }
});

export const {
    setTransactions,
    setCount,
    setTransactionsPerPage,
    setVisibility,
    setApprovalsForTransaction,
    setFetchingApprovalsForTransaction,
    updateTransactionDocuments,
    setIsTransactionsFetching,
    setIsUpdatingTransaction,
    setFetchingTransactionsError,
    setUpdatingTransactionError,
    setIsVisibilityFetching,
    setFetchingVisibilityError,
    setOptions,
    setIsDeletingTransaction,
    setDeletingTransactionError,
    setIsNextToSignTransactionFetching,
    setFetchingNextToSignTransactionError,
    setTransactionsSummary,
    setIsTransactionsSummaryFetching,
    setFetchingTransactionsSummaryError,
    setRecentlyUpdatedTransactions,
    setIsRecentlyUpdatedTransactionsFetching,
    setFetchingRecentlyUpdatedTransactionsError,
    setTransactionAuditEvents,
    setIsTransactionAuditEventsFetching
} = transactionsSlice.actions;

const SORT_FIELD_MAP = {
    [PACKAGE_SORT_OPTIONS.CREATION]: 'created_at',
    [PACKAGE_SORT_OPTIONS.COMPLETED]: 'completed_at',
    [PACKAGE_SORT_OPTIONS.DUE]: 'expires_at',
    [PACKAGE_SORT_OPTIONS.NAME]: 'name',
    [PACKAGE_SORT_OPTIONS.STATUS]: 'status'
};

function getMappedSort(sort, dir) {
    if (!sort) return undefined;
    const mapped = SORT_FIELD_MAP[sort] || sort;
    const isDesc = (dir || '').toLowerCase() === PACKAGE_DIR_OPTIONS.DESC;
    return isDesc ? `-${mapped}` : mapped;
}

const QUERY_MAPPING = {
    all: {scope: 'all'},
    inbox: {
        predefined: {
            all: {statuses: ['sent', 'completed']},
            sent: {statuses: ['sent']},
            completed: {statuses: ['completed']},
            expiringSoon: {statuses: ['sent'], expireSoon: true}
        }
    },
    draft: {
        predefined: {
            all: {statuses: ['draft', 'expired', 'declined']},
            draft: {statuses: ['draft']},
            declined: {statuses: ['declined']},
            expired: {statuses: ['expired']}
        }
    },
    archived: {statuses: ['archived']},
    trashed: {scope: 'trashed'},
    declined: {statuses: ['declined']},
    expired: {statuses: ['expired']},
    none: {
        predefined: {
            all: {statuses: ['draft', 'expired', 'sent', 'completed', 'declined']},
            draft: {statuses: ['draft']},
            declined: {statuses: ['declined']},
            expired: {statuses: ['expired']},
            sent: {statuses: ['sent']},
            completed: {statuses: ['completed']},
            expiringSoon: {statuses: ['sent'], expireSoon: true}
        }
    }
};

function getQueryScopeAndStatus(query, predefined) {
    const normalizedQuery = (query || '').toLowerCase() || 'none';
    const normalizedPredefined = predefined || '';
    const queryMap = QUERY_MAPPING[normalizedQuery];
    if (!queryMap) return {};

    if (queryMap.scope) {
        return {scope: queryMap.scope};
    }
    if (queryMap.statuses) {
        return {statuses: queryMap.statuses};
    }
    if (queryMap.predefined) {
        const selected = queryMap.predefined[normalizedPredefined] || queryMap.predefined.all;
        if (!selected) return {};
        const result = {};
        if (selected.statuses) result.statuses = selected.statuses;
        if (selected.expireSoon) result.expireSoon = true;
        return result;
    }
    return {};
}

function getIsAwaitingSignatures(predefined) {
    return (predefined || '').toLowerCase()
        === PACKAGE_PREDEFINED_OPTIONS.AWAITING_SIGNATURE.toLowerCase();
}

function getListParams({
    query, predefined, search, from, to, sort, dir, updatedSinceDays, visibility
}) {
    const params = {};
    if (getIsAwaitingSignatures(predefined)) {
        params.awaiting_signatures = 'true';
    } else {
        const {scope, statuses, expireSoon} = getQueryScopeAndStatus(query, predefined);
        if (scope) params.scope = scope;
        if (statuses) params['status[]'] = statuses;
        if (expireSoon) params.expire_soon = '1';
    }
    if (search) params.search = search;
    if (from) params.from = from;
    if (to) params.to = to;
    const mappedSort = getMappedSort(sort, dir);
    if (mappedSort) params.sort = mappedSort;
    if (updatedSinceDays) params.updated_since_days = updatedSinceDays;
    if (visibility === VISIBILITY_ACCOUNT) params.shared = 'true';
    else if (visibility === VISIBILITY_SENDER) params.shared = 'false';
    return params;
}

function getSenderViewFromRecipients(recipients, ownerId) {
    const ownerRecipient = (recipients || []).find((r) => getIsSenderRecipient(r, ownerId));
    if (!ownerRecipient) return null;
    return {
        id: ownerRecipient.id,
        email: ownerRecipient.email,
        firstName: ownerRecipient.first_name,
        lastName: ownerRecipient.last_name,
        company: ownerRecipient.organisation,
        title: ownerRecipient.title,
        user_id: ownerRecipient.user_id
    };
}

async function getEnrichedTransactions(baseTransactions, apiOptions = {}) {
    return Promise.all(baseTransactions.map(async (transaction) => {
        const recipientsSettled = await recipientApi.getRecipients(transaction.id, undefined, apiOptions)
            .then((value) => ({status: 'fulfilled', value}))
            .catch(() => ({status: 'rejected'}));
        const recipients = recipientsSettled.status === 'fulfilled' ? (recipientsSettled.value.data || []) : [];
        const sender = getSenderViewFromRecipients(recipients, transaction.owner_id);
        return {
            ...transaction,
            recipients,
            sender
        };
    }));
}

async function getTransactionsList({accountId, params, apiOptions}) {
    return transactionApi.getTransactions({accountId, params, ...apiOptions});
}

function getListCount(result) {
    return get(result, 'meta.total')
        ?? get(result, 'data.meta.total')
        ?? get(result, 'count')
        ?? get(result, 'data.count')
        ?? (Array.isArray(result?.data) ? result.data.length : 0);
}

export const fetchTransactions = ({
    headers, baseUrl, options: fetchOptions = {}, isSilent = false
} = {}) => async (dispatch, getState) => {
    const state = getState();
    const {transactions} = state;
    const accountId = selectActiveAccountId(state) || get(state, 'user.account.id');
    const {
        from, to, sort, dir, query, search, predefined, updatedSinceDays
    } = transactions.options;

    const apiOptions = {};
    if (headers) apiOptions.headers = headers;
    if (fetchOptions.timeout) apiOptions.timeout = fetchOptions.timeout;
    if (baseUrl) apiOptions.baseUrl = baseUrl;

    const params = getListParams({
        query, predefined, search, from, to, sort, dir, updatedSinceDays
    });

    if (!isSilent) dispatch(setIsTransactionsFetching(true));
    dispatch(setFetchingTransactionsError(null));

    try {
        const result = await getTransactionsList({accountId, params, apiOptions});
        const baseTransactions = Array.isArray(result?.data) ? result.data : [];
        const listCount = getListCount(result);
        const currentUser = get(state, 'user.user', {});
        const baseTransactionsWithSender = baseTransactions.map((transaction) => ({
            ...transaction,
            recipients: [],
            documents: [],
            sender: transaction.owner_id === currentUser.id
                ? {
                    id: currentUser.id,
                    email: currentUser.email,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    company: currentUser.organisation,
                    title: currentUser.title,
                    user_id: currentUser.id
                }
                : null
        }));

        if (!isSilent) {
            dispatch(setTransactions(baseTransactionsWithSender));
            dispatch(setCount(listCount));
            dispatch(setIsTransactionsFetching(false));
        }
        dispatch(setFetchingTransactionsError(null));

        const enrichedTransactions = await getEnrichedTransactions(baseTransactions, apiOptions);
        const recipientEmails = getRecipientsEmailsFromPackage(enrichedTransactions);
        await dispatch(fetchAvatarsByEmails({
            baseUrl, headers, emails: recipientEmails, options: fetchOptions
        }));

        dispatch(setTransactions(enrichedTransactions));
        if (isSilent) dispatch(setCount(listCount));
        return true;
    } catch (err) {
        if (!isSilent) dispatch(setIsTransactionsFetching(false));
        dispatch(setFetchingTransactionsError(getErrorMessage(err)));
        return false;
    }
};

export const fetchNextToSignTransaction = () => async (dispatch, getState) => {
    const accountId = selectActiveAccountId(getState()) || get(getState(), 'user.account.id');
    dispatch(setIsNextToSignTransactionFetching(true));
    dispatch(setFetchingNextToSignTransactionError(null));

    try {
        const result = await transactionApi.getTransactions({
            accountId,
            params: {
                from: 1,
                to: 1,
                sort: '-created_at',
                awaiting_signatures: 'true'
            }
        });
        dispatch(setIsNextToSignTransactionFetching(false));
        return Array.isArray(result?.data) ? result.data : [];
    } catch (err) {
        dispatch(setIsNextToSignTransactionFetching(false));
        dispatch(setFetchingNextToSignTransactionError(getErrorMessage(err)));
        return [];
    }
};

export const fetchVisibility = (packageId) => async (dispatch, getState) => {
    const {transactions} = getState();

    dispatch(setIsVisibilityFetching(true));
    dispatch(setFetchingVisibilityError(null));

    try {
        const result = await documentVisibilityApi.getTransactionVisibility(packageId);
        dispatch(setVisibility({
            ...transactions.visibility,
            [packageId]: result.configurations
        }));
        return true;
    } catch (err) {
        dispatch(setFetchingVisibilityError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsVisibilityFetching(false));
    }
};

export const fetchTransactionApprovals = (transactionId, documents = []) => async (dispatch) => {
    if (!documents?.length) {
        return false;
    }

    try {
        const documentApprovals = await getApprovalsForTransactionByDocumentId(
            transactionId,
            documents.map((doc) => doc.id)
        );

        dispatch(setApprovalsForTransaction({transactionId, documentApprovals}));
        return true;
    } catch {
        return false;
    }
};

export const fetchTransactionDocumentsAndApprovals = (transactionId) => async (dispatch, getState) => {
    const state = getState();
    const transaction = state.transactions.transactions.find(({id}) => id === transactionId);
    let documents = transaction?.documents || [];
    const hasApprovalsInitial = !isEmpty(state.transactions.approvalsByTransactionId[transactionId]);

    if (documents.length && hasApprovalsInitial) return true;

    dispatch(setFetchingApprovalsForTransaction({transactionId, isFetching: true}));
    try {
        if (!documents.length) {
            documents = await documentApi.getAllDocuments(transactionId).catch(() => []);
            dispatch(updateTransactionDocuments({transactionId, documents}));
        }
        if (documents.length) {
            await dispatch(fetchTransactionApprovals(transactionId, documents));
        }
        return true;
    } finally {
        dispatch(setFetchingApprovalsForTransaction({transactionId, isFetching: false}));
    }
};

function getAccountUserIds(state) {
    return {
        accountId: selectActiveAccountId(state) || get(state, 'user.account.id'),
        userId: get(state, 'user.user.id')
    };
}

export const archiveTransaction = (transactionId) => async (dispatch, getState) => {
    const {accountId, userId} = getAccountUserIds(getState());
    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdatingTransactionError(null));

    try {
        await transactionApi.archiveTransaction({accountId, userId, transactionId});
        dispatch(setIsUpdatingTransaction(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingTransaction(false));
        dispatch(setUpdatingTransactionError(getErrorMessage(err)));
        return false;
    }
};

export const unarchiveTransaction = (transactionId) => async (dispatch, getState) => {
    const {accountId, userId} = getAccountUserIds(getState());
    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdatingTransactionError(null));

    try {
        await transactionApi.unarchiveTransaction({accountId, userId, transactionId});
        dispatch(setIsUpdatingTransaction(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingTransaction(false));
        dispatch(setUpdatingTransactionError(getErrorMessage(err)));
        return false;
    }
};

export const restoreFromTrashTransaction = (transactionId) => async (dispatch, getState) => {
    const {accountId, userId} = getAccountUserIds(getState());
    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdatingTransactionError(null));

    try {
        await transactionApi.restoreTransaction({accountId, userId, transactionId});
        dispatch(setIsUpdatingTransaction(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingTransaction(false));
        dispatch(setUpdatingTransactionError(getErrorMessage(err)));
        return false;
    }
};

export const deleteTransaction = (transactionId) => async (dispatch, getState) => {
    const {accountId, userId} = getAccountUserIds(getState());
    dispatch(setIsDeletingTransaction(true));
    dispatch(setDeletingTransactionError(null));

    try {
        await transactionApi.deleteTransaction({accountId, userId, transactionId});
        dispatch(setIsDeletingTransaction(false));
        return true;
    } catch (err) {
        dispatch(setIsDeletingTransaction(false));
        dispatch(setDeletingTransactionError(getErrorMessage(err)));
        return false;
    }
};

const getSubscriptionPeriodStart = (planStartDate) => {
    const planStart = new Date(planStartDate);
    const now = new Date();
    const periodStart = new Date(planStart);
    periodStart.setFullYear(now.getFullYear());

    if (periodStart > now) {
        periodStart.setFullYear(now.getFullYear() - 1);
    }

    return periodStart;
};

const getIsReportWithinPeriod = (report, periodStart) => {
    const reportMonth = new Date(report?.year, (report?.month || 1) - 1, 1);
    const periodStartMonth = new Date(periodStart.getFullYear(), periodStart.getMonth(), 1);

    return reportMonth >= periodStartMonth;
};

export const fetchTransactionsSummary = () => async (dispatch, getState) => {
    const {account} = getState().user;
    const {id, licenses} = account || {};
    const license = get(licenses, '[0]', {});
    const transactionLimit = license.transactions;
    const isFreeUserPlan = get(license, 'plan.name', '').toLowerCase() === FREE_USERS_PLAN_NAME.toLowerCase();

    if (!id || !isFreeUserPlan || typeof transactionLimit !== 'number') {
        return true;
    }

    dispatch(setIsTransactionsSummaryFetching(true));

    try {
        const response = await transactionApi.getAccountTransactionReports({accountId: id});
        const periodStart = getSubscriptionPeriodStart(license.created);
        const transactionsInPeriod = get(response, 'data', [])
            .filter((report) => getIsReportWithinPeriod(report, periodStart))
            .reduce((count, report) => count + (report?.total || 0), 0);
        dispatch(setTransactionsSummary({allow_creation: transactionsInPeriod < transactionLimit}));
        dispatch(setIsTransactionsSummaryFetching(false));
        return true;
    } catch (err) {
        dispatch(setIsTransactionsSummaryFetching(false));
        dispatch(setFetchingTransactionsSummaryError(getErrorMessage(err)));
        return false;
    }
};

export const fetchRecentlyUpdatedTransactions = ({
    headers, baseUrl, options: fetchOptions = {}
} = {}) => async (dispatch, getState) => {
    const state = getState();
    const accountId = selectActiveAccountId(state) || get(state, 'user.account.id');

    const apiOptions = {};
    if (headers) apiOptions.headers = headers;
    if (fetchOptions.timeout) apiOptions.timeout = fetchOptions.timeout;
    if (baseUrl) apiOptions.baseUrl = baseUrl;

    dispatch(setIsRecentlyUpdatedTransactionsFetching(true));
    dispatch(setFetchingRecentlyUpdatedTransactionsError(null));

    try {
        const [allResult, awaitingResult] = await Promise.all([
            transactionApi.getTransactions({
                accountId,
                params: {
                    from: 1,
                    to: 15,
                    sort: '-updated_at',
                    scope: 'all'
                },
                ...apiOptions
            }),
            transactionApi.getTransactions({
                accountId,
                params: {
                    from: 1,
                    to: 15,
                    sort: '-updated_at',
                    awaiting_signatures: 'true'
                },
                ...apiOptions
            })
        ]);

        const allBase = Array.isArray(allResult?.data) ? allResult.data : [];
        const awaitingBase = Array.isArray(awaitingResult?.data) ? awaitingResult.data : [];

        const mergedUnique = [...allBase, ...awaitingBase].reduce((acc, transaction) => {
            if (!acc.find((t) => t.id === transaction.id)) {
                acc.push(transaction);
            }
            return acc;
        }, []);
        const sortedMerged = mergedUnique
            .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
            .slice(0, 30);

        const enrichedTransactions = await getEnrichedTransactions(sortedMerged, apiOptions);

        const recipientEmails = getRecipientsEmailsFromPackage(enrichedTransactions);
        await dispatch(fetchAvatarsByEmails({
            baseUrl, headers, emails: recipientEmails, options: fetchOptions
        }));

        dispatch(setRecentlyUpdatedTransactions(enrichedTransactions));
        dispatch(setIsRecentlyUpdatedTransactionsFetching(false));
        return enrichedTransactions;
    } catch (err) {
        dispatch(setIsRecentlyUpdatedTransactionsFetching(false));
        dispatch(setFetchingRecentlyUpdatedTransactionsError(getErrorMessage(err)));
        return false;
    }
};

export const fetchTransactionAuditEvents = (transactionId) => async (dispatch) => {
    dispatch(setIsTransactionAuditEventsFetching({transactionId, isFetching: true}));

    try {
        const result = await auditApi.getAudits(transactionId, {per_page: 100});
        const audits = Array.isArray(result.data) ? result.data : get(result, 'data.data', []);
        dispatch(setTransactionAuditEvents({
            transactionId,
            events: audits
        }));
        return true;
    } catch (err) {
        return false;
    } finally {
        dispatch(setIsTransactionAuditEventsFetching({transactionId, isFetching: false}));
    }
};

export const selectTransactions = (state) => state.transactions.transactions;
export const selectCount = (state) => state.transactions.count;
export const selectTransactionsPerPage = (state) => state.transactions.transactionsPerPage;
export const selectIsTransactionsFetching = (state) => state.transactions.isTransactionsFetching;
export const selectFetchingTransactionsError = (state) => state.transactions.fetchingTransactionsError;
export const selectVisibility = (state) => state.transactions.visibility;
export const selectApprovalsByTransactionId = (transactionId) => (state) => (
    state.transactions.approvalsByTransactionId[transactionId] || {}
);
export const selectIsFetchingApprovalsForTransaction = (transactionId) => (state) => (
    !!state.transactions.fetchingApprovalsByTransactionId[transactionId]
);
export const selectIsVisibilityFetching = (state) => state.transactions.isVisibilityFetching;
export const selectIsUpdatingTransaction = (state) => state.transactions.isUpdatingTransaction;
export const selectIsDeletingTransaction = (state) => state.transactions.isDeletingTransaction;
export const selectOptions = (state) => state.transactions.options;
export const selectIsNextToSignTransactionFetching = (state) => state.transactions.isNextToSignTransactionFetching;
export const selectFetchingNextToSignTransactionError = (state) => state.transactions.fetchingNextToSignTransactionError;
export const selectTransactionsSummary = (state) => state.transactions.transactionsSummary;
export const selectRecentlyUpdatedTransactions = (state) => state.transactions.recentlyUpdatedTransactions;
export const selectIsRecentlyUpdatedTransactionsFetching = (state) => state.transactions.isRecentlyUpdatedTransactionsFetching;
export const selectTransactionAuditEvents = (state) => state.transactions.transactionsAuditEvents;
export const selectIsTransactionAuditEventsFetching = (state) => state.transactions.isTransactionAuditEventsFetching;

export default transactionsSlice.reducer;
